import _ from 'lodash';
import { any, string, func, arrayOf, shape } from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import { styled } from '@mui/material/styles';
import { TextField, Box, Tab, Tabs, Paper, ClickAwayListener } from '@mui/material';
import AutocompleteMui, { createFilterOptions } from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import makeStyles from '@mui/styles/makeStyles';
import MenuItemMui from '@mui/material/MenuItem';
import SelectMui from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import FormField from 'widgets-v5/form-field';
/** import widgets components in the section above, Sort Lines Ascending */

import { VirtualizedListbox } from '../virtualized-listbox';
/** import single-line application imports in the section above, Sort Lines Ascending */

/**
 * Popper placement for the Autocomplete options listbox.
 * Matches Popper.js v2 placement values passed to MUI `componentsProps.popper.placement`.
 * @typedef {'auto'|'auto-start'|'auto-end'|'top'|'top-start'|'top-end'|'bottom'|'bottom-start'|'bottom-end'|'right'|'right-start'|'right-end'|'left'|'left-start'|'left-end'} ListboxPlacement
 */

/**
 * MUI Autocomplete `componentsProps` when `listboxPlacement` is set (flip disabled).
 * @param {ListboxPlacement} listboxPlacement
 * @returns {{ popper: Object }}
 */
function getListboxPlacementPopperProps(listboxPlacement) {
    return {
        popper: {
            placement: listboxPlacement,
            modifiers: [
                { name: 'flip', enabled: false },
                {
                    name: 'preventOverflow',
                    enabled: true,
                    options: {
                        altAxis: true,
                        tether: true,
                        padding: 8,
                    },
                },
            ],
        },
    };
}

const Select = styled(SelectMui)({
    '&.MuiSelect-select': {
        padding: 13,
        backgroundColor: 'white',
        borderRadius: 'inherit',
    },
});

const Autocomplete = styled(AutocompleteMui)({
    '&.MuiAutocomplete-root': {
        padding: '4.5px!important',
        backgroundColor: 'white',
    },
});

/**
 * Select dropdown row — shared by {@link SimpleSelect} and {@link CustomContentSelect}.
 *
 * Rows fill the menu paper width so the full row is clickable. Paper width is pinned to the
 * FormControl anchor via {@link useAnchorMenuWidth} so rich option content does not widen the menu.
 */
const MenuItem = styled(MenuItemMui)({
    '&.MuiMenuItem-root': {
        width: '100%',
        maxWidth: '100%',
        whiteSpace: 'normal',
        boxSizing: 'border-box',
    },
    // Custom option content (e.g. creative preview Grid) fills the row without expanding the menu
    '&.MuiMenuItem-root > *': {
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
});

const useStyles = makeStyles(() => ({
    notAllowed: {
        opacity: 0.55,
        color: 'rgba(0, 0, 0, 0.6)',
    },
}));

/**
 * Pins MUI Select menu paper width to the FormControl anchor so dropdown matches the field width.
 *
 * @returns {{ anchorRef: React.RefObject, menuProps: Object, onOpen: Function }}
 */
function useAnchorMenuWidth() {
    const anchorRef = useRef(null);
    const [menuWidth, setMenuWidth] = useState(undefined);

    const menuProps = {
        PaperProps: {
            style: menuWidth ? { width: menuWidth, maxWidth: menuWidth } : undefined,
        },
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
        },
        transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
        },
    };

    const onOpen = () => {
        setMenuWidth(anchorRef.current?.clientWidth);
    };

    return { anchorRef, menuProps, onOpen };
}

export function SimpleSelect(props) {
    const {
        label,
        required,
        value,
        onChange,
        options,
        placeholder,
        className,
        margin,
        error,
        disabled,
    } = props;

    const { anchorRef, menuProps, onOpen } = useAnchorMenuWidth();

    return (
        <div className={className}>
            <FormControl
                ref={anchorRef}
                variant="outlined"
                fullWidth
                margin={margin}
                error={error}
                disabled={disabled ? disabled : false}
            >
                {label && (
                    <Box mb={1}>
                        <FormLabel component="legend" focused={false} required={required}>
                            {label}
                        </FormLabel>
                    </Box>
                )}
                <Select
                    id="demo-customized-select"
                    value={value}
                    disabled={disabled}
                    onChange={onChange}
                    onOpen={onOpen}
                    MenuProps={menuProps}
                    displayEmpty
                >
                    <MenuItem value="" disabled>
                        <Typography color="textSecondary">{placeholder}</Typography>
                    </MenuItem>
                    {_.map(options, option => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled ? option.disabled : false}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </div>
    );
}

export function CustomContentSelect(props) {
    const {
        label,
        required,
        value,
        onChange,
        options,
        placeholder,
        className,
        margin,
        error,
    } = props;

    const { anchorRef, menuProps, onOpen } = useAnchorMenuWidth();

    return (
        <div className={className}>
            <FormControl ref={anchorRef} variant="outlined" fullWidth margin={margin} error={error}>
                {label && (
                    <Box mb={1}>
                        <FormLabel component="legend" focused={false} required={required}>
                            {label}
                        </FormLabel>
                    </Box>
                )}
                <Select
                    id="demo-customized-select"
                    value={value ? value : ''}
                    onChange={onChange}
                    onOpen={onOpen}
                    MenuProps={menuProps}
                    displayEmpty
                >
                    <MenuItem value="" disabled>
                        {placeholder}
                    </MenuItem>
                    {_.map(options, option => (
                        <MenuItem key={option.value} value={option.value}>
                            {_.isFunction(option.content) ? option.content() : option.content}
                        </MenuItem>
                    ))}
                </Select>
                {error && <FormHelperText>{error}</FormHelperText>}
            </FormControl>
        </div>
    );
}

//option.value and value must be string type
/**
 * Single-select Autocomplete for `{ label, value }` option lists.
 *
 * @param {Object} props
 * @param {ListboxPlacement} [props.listboxPlacement] - Fixed Popper placement for the
 *   options dropdown. When set, disables Popper flip so the list stays at this placement
 *   (e.g. does not jump above the input when the virtualized list is tall). When omitted,
 *   MUI Autocomplete uses its default Popper behavior (flip enabled).
 *   Allowed values:
 *   - `'auto'`, `'auto-start'`, `'auto-end'` — Popper chooses side
 *   - `'top'`, `'top-start'`, `'top-end'` — above the input
 *   - `'bottom'`, `'bottom-start'`, `'bottom-end'` — below the input
 *   - `'left'`, `'left-start'`, `'left-end'` — left of the input
 *   - `'right'`, `'right-start'`, `'right-end'` — right of the input
 *   Campaign filter drawer uses `'bottom-start'`.
 * @param {Function} [props.filterOptions] - Custom MUI Autocomplete filter callback
 * @param {string} [props.id] - Stable DOM id for the combobox input (for E2E selectors); passed to Autocomplete so MUI keeps label/aria wiring in sync
 */
export function SingleSelect({
    value,
    options,
    onChange,
    placeholder,
    disabled,
    margin,
    error,
    label,
    required,
    listboxPlacement,
    filterOptions,
    id,
}) {
    const filteredOptions = _.filter(options, option => _.includes(value, option.value));
    const formattedValue =
        _.find(filteredOptions, option => option.value === value) || _.last(filteredOptions);

    return (
        <FormControl
            variant="outlined"
            fullWidth
            margin={margin}
            error={error}
            disabled={disabled ? disabled : false}
        >
            {label && (
                <FormLabel component="legend" focused={false} required={required}>
                    {label}
                </FormLabel>
            )}
            <Autocomplete
                {...(id ? { id } : {})}
                value={formattedValue || ''}
                options={options}
                disabled={disabled}
                filterOptions={filterOptions}
                componentsProps={
                    listboxPlacement ? getListboxPlacementPopperProps(listboxPlacement) : undefined
                }
                isOptionEqualToValue={(option, value) => {
                    if (value === '') {
                        return true;
                    }
                    return value.value === option.value;
                }}
                getOptionLabel={option => option.label || ''}
                renderOption={(props, option) => {
                    // eslint-disable-next-line no-unused-vars
                    const { key, ...rest } = props;
                    return (
                        <div key={option.value} {...rest}>
                            {option.label}
                        </div>
                    );
                }}
                onChange={(event, value) => {
                    if (value !== null) {
                        onChange(value.value);
                    } else {
                        onChange(null);
                    }
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        inputProps={{
                            ...params.inputProps,
                            placeholder: placeholder,
                        }}
                        variant="outlined"
                        fullWidth
                        error={error}
                    />
                )}
            />
            {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    );
}

export function GroupedSingleSelect({
    options,
    placeholder,
    disabled,
    error,
    label,
    required,
    onChange,
    value,
}) {
    const formattedValue = _.find(options, option => _.includes(value, option.value));
    const newOptions = _.map(options, option => {
        const firstLetter = option.group[0].toUpperCase();
        return { firstLetter, ...option };
    });
    const optionsSorted = newOptions.sort((a, b) => {
        if (a.group) {
            return b.firstLetter.localeCompare(a.firstLetter);
        }
        return _.trim(a.label);
    });

    return (
        <div>
            <FormControl
                fullWidth
                variant="outlined"
                error={error}
                disabled={disabled ? disabled : false}
            >
                {label && (
                    <FormLabel component="legend" focused={false} required={required}>
                        {label}
                    </FormLabel>
                )}
                <Autocomplete
                    id="grouped-demo"
                    options={optionsSorted}
                    value={formattedValue}
                    groupBy={option => option.group}
                    getOptionLabel={option => option.label}
                    onChange={(event, option) => {
                        onChange(option.value);
                    }}
                    renderInput={params => (
                        <TextField
                            {...params}
                            inputProps={{ ...params.inputProps, placeholder: placeholder }}
                            variant="outlined"
                            fullWidth
                            error={error}
                        />
                    )}
                />
            </FormControl>
        </div>
    );
}

/**
 * Multi-select Autocomplete for `{ label, value }` option lists.
 *
 * @param {Object} props
 * @param {Array<{ label: string, value: string }>} props.options
 * @param {string[]} props.value - Selected option values
 * @param {Function} props.onChange - Called with updated value array
 * @param {boolean} [props.virtualized] - When true, replaces the default listbox with
 *   {@link VirtualizedListbox} (react-window). MUI recommends virtualizing Autocomplete
 *   dropdowns with large option sets; without it, opening the panel mounts every option
 *   in the DOM and can freeze the UI. Defaults to false for backward compatibility.
 * @param {Function} [props.filterOptions] - Custom MUI Autocomplete filter callback
 * @param {ListboxPlacement} [props.listboxPlacement] - Fixed Popper placement for the
 *   options dropdown. When set, disables Popper flip so the list stays at this placement
 *   (e.g. does not jump above the input when the virtualized list is tall). When omitted,
 *   MUI Autocomplete uses its default Popper behavior (flip enabled).
 *   Allowed values:
 *   - `'auto'`, `'auto-start'`, `'auto-end'` — Popper chooses side
 *   - `'top'`, `'top-start'`, `'top-end'` — above the input
 *   - `'bottom'`, `'bottom-start'`, `'bottom-end'` — below the input
 *   - `'left'`, `'left-start'`, `'left-end'` — left of the input
 *   - `'right'`, `'right-start'`, `'right-end'` — right of the input
 *   Campaign filter drawer uses `'bottom-start'`.
 * @see {@link https://mui.com/material-ui/react-autocomplete/#virtualization MUI Autocomplete — Virtualization}
 * @see {@link VirtualizedListbox}
 */
export function MultiSelect({
    options,
    maxAllowedOptions,
    value,
    onChange,
    placeholder,
    required,
    error,
    errorMessage,
    disabled,
    fullWidth,
    formFullWidth = true,
    margin,
    label,
    formControlClass,
    preHelperText,
    sx,
    filterOptions,
    virtualized,
    listboxPlacement,
}) {
    const formattedValue = _.filter(options, option => _.includes(value, option.value));
    const isRequired = required ? required : false;
    if (isRequired && formattedValue.length < 1) {
        options.map(item => formattedValue.push(item));
    }
    const classes = useStyles();

    return (
        <Box sx={{ mt: 1, ...sx }}>
            <FormControl
                variant="outlined"
                fullWidth={formFullWidth}
                margin={margin}
                error={error}
                className={formControlClass}
            >
                {label && (
                    <FormLabel component="legend" focused={false} required={required}>
                        {label}
                    </FormLabel>
                )}
                {preHelperText && <FormHelperText>{preHelperText}</FormHelperText>}
                <Autocomplete
                    value={formattedValue}
                    multiple
                    disabled={disabled}
                    options={options}
                    disableCloseOnSelect
                    fullWidth={fullWidth}
                    filterOptions={filterOptions}
                    componentsProps={
                        listboxPlacement
                            ? getListboxPlacementPopperProps(listboxPlacement)
                            : undefined
                    }
                    // Virtualize dropdown rows for large option lists (dashboard client filter).
                    ListboxComponent={virtualized ? VirtualizedListbox : undefined}
                    getOptionLabel={option => option.label}
                    renderOption={(props, option, { selected }) => {
                        // eslint-disable-next-line no-unused-vars
                        const { key, ...rest } = props;
                        const isDisabled =
                            option.disabled ||
                            (maxAllowedOptions !== undefined &&
                                formattedValue.length >= maxAllowedOptions &&
                                !selected);

                        const listItem = (
                            <ListItem component="div" key={option.value} {...rest}>
                                <Checkbox
                                    required={isRequired}
                                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                    color="primary"
                                    disabled={isDisabled}
                                />
                                <ListItemText
                                    primary={option.label}
                                    secondary={option.description}
                                />
                            </ListItem>
                        );

                        if (isDisabled) {
                            const title =
                                option.disabled || maxAllowedOptions === 0
                                    ? 'This option is disabled'
                                    : `You can select a maximum of ${maxAllowedOptions} options`;

                            return (
                                <Tooltip title={title} placement="top-start">
                                    <div className={classes.notAllowed}>{listItem}</div>
                                </Tooltip>
                            );
                        }

                        return listItem;
                    }}
                    onChange={(event, selectedOptions) => {
                        const enabledOptions = _.filter(
                            selectedOptions,
                            option => !option.disabled
                        );
                        const values = _.map(enabledOptions, option => option.value);
                        if (
                            maxAllowedOptions !== undefined &&
                            enabledOptions.length > maxAllowedOptions
                        ) {
                            return;
                        }

                        onChange(values);
                    }}
                    renderInput={params => (
                        <TextField
                            {...params}
                            inputProps={{ ...params.inputProps, placeholder: placeholder }}
                            variant="outlined"
                            fullWidth
                            error={error}
                        />
                    )}
                />
                {error && <FormHelperText>{errorMessage}</FormHelperText>}
            </FormControl>
        </Box>
    );
}

export function GroupedMultiSelect({
    options,
    value,
    onChange,
    placeholder,
    required,
    error,
    disabled,
}) {
    const formattedValue = _.filter(options, option => _.includes(value, option.value));
    const isRequired = required ? required : false;
    if (isRequired && formattedValue.length < 1) {
        _.each(options, item => formattedValue.push(item));
    }

    const optionsSorted = _.sortBy(options, [
        option => option.group || '',
        option => (option.disabled ? 1 : 0),
        option => _.trim(option.label),
    ]);

    const filterOptions = createFilterOptions({
        stringify: option => `${option.label}${option.key}`,
    });

    return (
        <Box mt={1}>
            <Autocomplete
                value={formattedValue}
                multiple
                disabled={disabled}
                disableCloseOnSelect
                options={optionsSorted}
                filterOptions={filterOptions}
                groupBy={option => option.group}
                getOptionLabel={option => option.label}
                renderOption={(props, option, { selected }) => {
                    // eslint-disable-next-line no-unused-vars
                    const { key, ...rest } = props;
                    return (
                        <ListItem key={option.value} {...rest}>
                            <Checkbox
                                required={isRequired}
                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                style={{ marginRight: 8 }}
                                checked={selected}
                                color="primary"
                            />
                            <ListItemText primary={option.label} secondary={option.description} />
                        </ListItem>
                    );
                }}
                onChange={(event, selectedOptions) => {
                    const values = _.map(selectedOptions, option => option.value);
                    onChange(values);
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        inputProps={{ ...params.inputProps, placeholder: placeholder }}
                        variant="outlined"
                        fullWidth
                        error={error}
                    />
                )}
            />
        </Box>
    );
}

export function GroupedMultiSelectTabs({
    options,
    value,
    onChange,
    placeholder,
    required,
    showErrors,
    errors,
    disabled,
    tabs,
    defaultTab,
    tabField,
}) {
    const classes = useStyles();
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [open, setOpen] = useState(false);
    const listBoxRef = useRef(null); // Reference for listbox to manage scroll position
    const scrollPositionRef = useRef(0); // Keep track of the scroll position

    const formattedValue = _.filter(options, option => _.includes(value, option.value));

    const isRequired = required ? required : false;
    if (isRequired && formattedValue.length < 1) {
        _.each(options, item => formattedValue.push(item));
    }

    const optionsSorted = _.sortBy(options, [
        option => option.group || '',
        option => (option.disabled ? 1 : 0),
        option => _.trim(option.label),
    ]);

    useEffect(() => {
        if (listBoxRef.current) {
            listBoxRef.current.scrollTop = scrollPositionRef.current;
        }
    }, [optionsSorted]);

    const filterOptions = createFilterOptions({
        stringify: option => `${option.label}${option.key}`,
    });

    return (
        <ClickAwayListener
            onClickAway={() => {
                setOpen(false);
            }}
        >
            <Box mt={1}>
                <FormField showErrors={showErrors} errors={errors}>
                    <Autocomplete
                        value={formattedValue}
                        multiple
                        open={open}
                        disabled={disabled}
                        disableCloseOnSelect
                        options={optionsSorted.filter(option => option[tabField] === activeTab)}
                        filterOptions={filterOptions}
                        groupBy={option => option.group}
                        getOptionLabel={option => option.label}
                        renderOption={(props, option, { selected }) => {
                            // eslint-disable-next-line no-unused-vars
                            const { key, ...rest } = props;
                            const listItem = (
                                <ListItem key={option.value} {...rest}>
                                    <Checkbox
                                        required={isRequired}
                                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                        color="primary"
                                        disabled={option.disabled}
                                    />
                                    <ListItemText
                                        primary={option.label}
                                        secondary={option.description}
                                    />
                                </ListItem>
                            );

                            if (option.disabled) {
                                return (
                                    <Tooltip title="This option is disabled" placement="top-start">
                                        <div className={classes.notAllowed}>{listItem}</div>
                                    </Tooltip>
                                );
                            }

                            return listItem;
                        }}
                        onChange={(event, selectedOptions) => {
                            const enabledOptions = _.filter(
                                selectedOptions,
                                option => !option.disabled
                            );
                            const values = _.map(enabledOptions, option => option.value);
                            onChange(values);
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                inputProps={{ ...params.inputProps, placeholder: placeholder }}
                                variant="outlined"
                                onClick={() => setOpen(true)}
                                fullWidth
                                error={errors}
                            />
                        )}
                        ListboxComponent={listboxProps => (
                            <ul
                                {...listboxProps}
                                ref={listBoxRef}
                                onScroll={() => {
                                    // Save scroll position on scroll
                                    if (listBoxRef.current) {
                                        scrollPositionRef.current = listBoxRef.current.scrollTop;
                                    }
                                }}
                            />
                        )}
                        PaperComponent={({ children, ...props }) => {
                            return tabs.length >= 1 ? (
                                <Paper {...props}>
                                    <Tabs
                                        value={activeTab}
                                        onChange={(event, newValue) => {
                                            setActiveTab(newValue);
                                        }}
                                        variant="fullWidth"
                                        indicatorColor="primary"
                                        textColor="primary"
                                    >
                                        {tabs.map(({ value, label }) => (
                                            <Tab key={value} label={label} value={value} />
                                        ))}
                                    </Tabs>
                                    <Box p={1}>{children}</Box>
                                </Paper>
                            ) : (
                                <Paper {...props}>{children}</Paper>
                            );
                        }}
                    />
                </FormField>
            </Box>
        </ClickAwayListener>
    );
}

MultiSelect.propTypes = {
    values: arrayOf(string),
    options: arrayOf(
        shape({
            label: string,
            value: any,
        })
    ),
    onChange: func,
};

export function OptionCreatableSelect({ options, value, onChange, placeholder, error }) {
    const filter = createFilterOptions();

    return (
        <Autocomplete
            value={value}
            onChange={(event, value) => {
                if (value !== null) {
                    onChange(value.value);
                } else {
                    onChange(null);
                }
            }}
            filterOptions={(options, params) => {
                const filtered = filter(options, params);
                if (params.inputValue !== '') {
                    const newOption = [
                        {
                            value: params.inputValue,
                            label: params.inputValue,
                        },
                    ];
                    return filtered.concat(newOption);
                }
                return filtered;
            }}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            options={options}
            getOptionLabel={option => {
                // Value selected with enter, right from the input
                if (typeof option === 'string') {
                    return option;
                }
                // Add option created dynamically
                if (option.inputValue) {
                    return option.inputValue;
                }
                // Regular option
                return option.label;
            }}
            renderOption={option => option.label}
            freeSolo
            renderInput={params => (
                <TextField
                    {...params}
                    inputProps={{ ...params.inputProps, placeholder: placeholder }}
                    variant="outlined"
                    fullWidth
                    error={error}
                />
            )}
        />
    );
}

OptionCreatableSelect.propTypes = {
    value: any,
    options: arrayOf(
        shape({
            label: string,
            value: any,
        })
    ),
    onChange: func,
};
